import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Check, Mail, Loader2 } from "lucide-react";

import SEO from "@/components/SEO";
import { Container, SectionLabel } from "@/components/primitives";
import { SITE } from "@/data/site";

const CHANNELS = [
  { label: "Email", value: SITE.email, href: `mailto:${SITE.email}` },
  { label: "LinkedIn", value: "in/vivek-chandra", href: SITE.linkedin },
  { label: "GitHub", value: "vivek1389", href: SITE.github },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success

  const update = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((err) => ({ ...err, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) next.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      next.email = "Please enter a valid email address.";
    if (!form.message.trim()) next.message = "Tell me a little about your project.";
    return next;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const next = validate();
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }
    setStatus("sending");
    // Static site: simulate submission, no backend required.
    setTimeout(() => setStatus("success"), 900);
  };

  const inputBase =
    "w-full rounded-none border-0 border-b border-white/15 bg-transparent px-0 py-3 text-primary-ink placeholder:text-muted-ink focus:border-accent focus:outline-none focus:ring-0 transition-colors";

  return (
    <>
      <SEO
        title="Contact"
        description="Let's build something great together. Whether you're building a new product, modernizing an application, or exploring AI, reach out to Vivek Chandra."
        path="/contact"
      />

      <section className="relative pt-36 md:pt-44" data-testid="contact-page">
        <div className="pointer-events-none absolute right-0 top-32 h-[380px] w-[520px] rounded-full bg-accent/10 blur-[150px]" />
        <Container className="relative">
          <div className="grid gap-16 lg:grid-cols-12">
            {/* Left: intro + channels */}
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              >
                <SectionLabel>Contact</SectionLabel>
                <h1 className="mt-8 font-cabinet text-4xl font-medium leading-[1.05] tracking-tight text-primary-ink sm:text-5xl">
                  Let's build something great together.
                </h1>
                <p className="mt-6 text-lg leading-relaxed text-secondary-ink">
                  Whether you're building a new product, modernizing an existing
                  application, exploring AI, or looking for an experienced
                  software engineer, I'd love to hear about your project.
                </p>

                <div className="mt-12 space-y-px overflow-hidden border border-white/10">
                  {CHANNELS.map((c) => (
                    <a
                      key={c.label}
                      href={c.href}
                      target={c.href.startsWith("http") ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      data-testid={`contact-channel-${c.label.toLowerCase()}`}
                      className="group flex items-center justify-between bg-ink-base p-5 transition-colors hover:bg-ink-surface"
                    >
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-ink">
                          {c.label}
                        </p>
                        <p className="mt-1 text-primary-ink">{c.value}</p>
                      </div>
                      <ArrowUpRight className="h-5 w-5 text-muted-ink transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-accent" />
                    </a>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Right: form */}
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
                className="border border-white/10 p-8 md:p-12"
              >
                <AnimatePresence mode="wait">
                  {status === "success" ? (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex min-h-[420px] flex-col items-center justify-center text-center"
                      data-testid="contact-success"
                      role="status"
                      aria-live="polite"
                    >
                      <div className="flex h-14 w-14 items-center justify-center rounded-full border border-accent/40 bg-accent/10">
                        <Check className="h-6 w-6 text-accent" />
                      </div>
                      <h2 className="mt-8 font-cabinet text-3xl font-medium tracking-tight text-primary-ink">
                        Message received.
                      </h2>
                      <p className="mt-3 max-w-md leading-relaxed text-secondary-ink">
                        Thanks for reaching out, {form.name.split(" ")[0] || "there"}.
                        I read every message personally and will get back to you
                        shortly. For anything urgent, email me directly at{" "}
                        <a
                          href={`mailto:${SITE.email}`}
                          className="text-primary-ink underline decoration-white/20 underline-offset-4 hover:decoration-accent"
                        >
                          {SITE.email}
                        </a>
                        .
                      </p>
                      <button
                        type="button"
                        data-testid="contact-reset"
                        onClick={() => {
                          setForm({ name: "", email: "", message: "" });
                          setStatus("idle");
                        }}
                        className="mt-8 font-mono text-xs uppercase tracking-[0.15em] text-muted-ink transition-colors hover:text-primary-ink"
                      >
                        Send another message
                      </button>
                    </motion.div>
                  ) : (
                    <motion.form
                      key="form"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onSubmit={onSubmit}
                      noValidate
                      data-testid="contact-form"
                      className="space-y-8"
                    >
                      <div>
                        <label
                          htmlFor="name"
                          className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-ink"
                        >
                          Name
                        </label>
                        <input
                          id="name"
                          type="text"
                          value={form.name}
                          onChange={update("name")}
                          placeholder="Your name"
                          data-testid="contact-input-name"
                          className={inputBase}
                        />
                        {errors.name && (
                          <p className="mt-2 text-sm text-red-400" data-testid="error-name">
                            {errors.name}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="email"
                          className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-ink"
                        >
                          Email
                        </label>
                        <input
                          id="email"
                          type="email"
                          value={form.email}
                          onChange={update("email")}
                          placeholder="you@company.com"
                          data-testid="contact-input-email"
                          className={inputBase}
                        />
                        {errors.email && (
                          <p className="mt-2 text-sm text-red-400" data-testid="error-email">
                            {errors.email}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="message"
                          className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-ink"
                        >
                          Message
                        </label>
                        <textarea
                          id="message"
                          rows={5}
                          value={form.message}
                          onChange={update("message")}
                          placeholder="Tell me about your project, timeline, and what you're hoping to achieve."
                          data-testid="contact-input-message"
                          className={`${inputBase} resize-none`}
                        />
                        {errors.message && (
                          <p className="mt-2 text-sm text-red-400" data-testid="error-message">
                            {errors.message}
                          </p>
                        )}
                      </div>

                      <button
                        type="submit"
                        disabled={status === "sending"}
                        data-testid="contact-submit-btn"
                        className="group inline-flex w-full items-center justify-center gap-2 bg-accent px-7 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-accent-hover disabled:opacity-60 sm:w-auto"
                      >
                        {status === "sending" ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Sending
                          </>
                        ) : (
                          <>
                            <Mail className="h-4 w-4" />
                            Send Message
                          </>
                        )}
                      </button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </div>
        </Container>
      </section>

      <div className="h-24 md:h-32" />
    </>
  );
}
